import { useQuery } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { useConnectionStore } from '../stores/connectionStore';

/** The modules a facility pays for, from Tech Log (tblTL_Facilities *_Allow). */
export interface FacilityLicenseModules {
  dietech: boolean;
  dietechLite: boolean;
  production: boolean;
  recipe: boolean;
  pharmacy: boolean;
  purchasing: boolean;
  department: boolean;
  budget: boolean;
  nutritionalAssessment: boolean;
  planOfCare: boolean;
}

/** `known` is false when the API could not reach Tech Log and had no earlier answer. */
export interface FacilityLicenses {
  known: boolean;
  modules: FacilityLicenseModules;
}

/**
 * The signed-in connection's licences (GET /api/licenses). The API caches Tech Log's answer for
 * 10 minutes, so this does too. Until it arrives, and if it fails, nothing counts as licensed.
 */
export function useFacilityLicenses() {
  const connectionId = useConnectionStore((s) => s.selectedConnection?.connectionId ?? 0);
  return useQuery({
    queryKey: ['facilityLicenses', connectionId],
    queryFn: async () => {
      const { data } = await apiClient.get<FacilityLicenses>(API.licenses);
      return data;
    },
    enabled: connectionId > 0,
    staleTime: 10 * 60_000,
  });
}

/** True only when Tech Log says the facility is licensed for purchasing. */
export function usePurchasingLicensed(): boolean {
  const { data } = useFacilityLicenses();
  return !!data?.known && !!data.modules?.purchasing;
}
